import styled from "@emotion/styled";
import {
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import themes from "../../themes";
import { Footer } from "../organisms/Footer";
import WaitingProcessDialog from "../molecules/WaitingProcessDialog";
import { ChainId } from "@usedapp/core";
import { WalletButton } from "../atoms/WalletButton";
import { BigNumberish, ethers } from "ethers";
import ChainModal from "../molecules/ChainModal";
import MultipleDialog from "../molecules/MultipleDialog";
import { Smartphone } from "@mui/icons-material";
import Header from "../organisms/Header";
import DonationDialog from "../molecules/DonationDialog";

interface PageTemplateProps {
  readonly isClaimable: boolean;
  readonly merkleProof: string[];
  readonly isConnected: boolean;
  readonly transactionStatus: any;
  readonly buyBundleTransactionStatus: any;
  readonly mintStatus: any;
  readonly isPurchasing: boolean;
  readonly chainId: ChainId | undefined;
  readonly account: string | null | undefined;
  readonly claim: (...args: any[]) => Promise<void>;
  readonly buyBundle: (...args: any[]) => Promise<void>;
  readonly mint: (...args: any[]) => Promise<void>;
  readonly getTokenId?: string | number;
  readonly getTokenIds?: string[] | number[];
  readonly isOnSale?: boolean;
  readonly isSoldOut?: boolean;
  readonly price?: BigNumberish;
  readonly amountNumber: number;
}

export const PageTemplate = ({
  claim,
  mint,
  price,
  merkleProof,
  ...props
}: PageTemplateProps) => {
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [multipleModalOpen, setMultipleModalOpen] = useState(false);

  const onClickMintButton = useCallback((tokenId) => {
    mint(tokenId, { value: price });
  }, [mint, price]);

  const displayButton = useMemo(() => {
    if (props.isOnSale) {
      if (props.isSoldOut) {
        return (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                window.open(
                  "https://opensea.io/collection/chimney-town-dao",
                  "_blank"
                );
              }}
              disableElevation
              fullWidth
              style={{ color: "white" }}
            >
              OpenSeaを開く
            </Button>
          </Stack>
        );
      } else {
        return (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="contained"
              size="large"
              onClick={() => onClickMintButton(props.getTokenId)}
              disableElevation
              fullWidth
              style={{ color: "white" }}
            >
              NFTを購入する
            </Button>
          </Stack>
        );
      }
    } else {
      return (
        <Button
          variant="contained"
          size="large"
          disableElevation
          fullWidth
          disabled
        >
          利用できません
        </Button>
      );
    }
  }, [props.isSoldOut, props.isOnSale, props.getTokenId]);

  const ref = React.createRef<HTMLDivElement>();
  return (
    <>
      <ChainModal chainId={props.chainId} />
      <Header />
      <ContentDiv>
        <StyledSection style={{ marginBottom: 54 }}>
          <PageTitle>CHIMNEY TOWN DAO</PageTitle>
        </StyledSection>
        <StyledSection ref={ref} style={{ paddingTop: 20 }}>
          <NftImageWrapper>
            <NftImg src="/img/image.jpg" alt="NFT image" />
          </NftImageWrapper>
          <div>
            <PriceTypo>
              {price !== undefined && props.isOnSale && (
                <>
                  {`${ethers.utils.formatUnits(price ?? 0, 18)}`}
                  <span className={"ethTxt"}>ETH</span>
                  <span className="amount">({props.amountNumber} / 10000)</span>
                </>
              )}
            </PriceTypo>
            {props.isConnected ? (
              displayButton
            ) : (
              <WalletButton account={props.account} />
            )}
            {
              // @ts-ignore
              !window.ethereum?.isMetaMask && (
                <MetaMaskWrapper>
                  <p style={{ textAlign: "center" }}>or</p>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      window.open(
                        `https://metamask.app.link/dapp/${window.location.host}/#/`
                      );
                    }}
                    fullWidth
                    disableElevation
                    style={{ marginBottom: 40 }}
                    startIcon={<Smartphone />}
                  >
                    メタマスクを使う
                  </Button>
                </MetaMaskWrapper>
              )
            }
          </div>
        </StyledSection>
        <SnsButtonWrapper>
          <IconButton
            aria-label="opensea"
            onClick={() =>
              window.open(
                "https://opensea.io/collection/chimney-town-dao",
                "_blank"
              )
            }
            style={{ marginRight: 8 }}
          >
            <img src="/img/opensea.svg" style={{ width: "100%" }} />
          </IconButton>
          <IconButton
            aria-label="discord"
            onClick={() => window.open("https://discord.gg/87e5SwSBY9")}
            style={{ marginRight: 8 }}
          >
            <img src="/img/discord_icon.png" alt="Discord" />
          </IconButton>
        </SnsButtonWrapper>
      </ContentDiv>
      <Footer />
      <WaitingProcessDialog
        account={props.account}
        transactionStatus={props.transactionStatus}
        isPurchasing={props.isPurchasing}
        chainId={props.chainId}
        getTokenId={props.getTokenId}
      />
      <WaitingProcessDialog
        account={props.account}
        transactionStatus={props.buyBundleTransactionStatus}
        isPurchasing={props.isPurchasing}
        chainId={props.chainId}
        getTokenId={props.getTokenId}
        getTokenIds={props.getTokenIds}
      />
      <WaitingProcessDialog
        account={props.account}
        transactionStatus={props.mintStatus}
        isPurchasing={props.isPurchasing}
        chainId={props.chainId}
        getTokenId={props.getTokenId}
        getTokenIds={props.getTokenIds}
      />
      <MultipleDialog
        open={multipleModalOpen}
        setModalOpen={setMultipleModalOpen}
        buyBundle={props.buyBundle}
        price={price}
      />
      <DonationDialog
        merkleProof={merkleProof}
        open={claimModalOpen}
        setModalOpen={setClaimModalOpen}
        claim={claim}
        getTokenId={props.getTokenId}
      />
    </>
  );
};

const PageTitle = styled.h1`
  font-family: "Raleway", sans-serif;
  font-size: 2.375rem;
  color: ${themes.palette.primary.main};
  text-align: center;
  margin-bottom: 26px;
`;

const ContentDiv = styled.div`
  padding-top: ${themes.spacing(8)};
  padding-left: 1rem;
  padding-right: 1rem;
  padding-bottom: ${themes.spacing(8)};
  max-width: 632px;
  margin-left: auto;
  margin-right: auto;
`;

const FooterWrapper = styled.div`
  background-image: url("/img/ft-bg.png");
  background-repeat: repeat-x;
  padding-top: 160px;
  background-position: top;
`;

const StyledSection = styled.section`
  margin-bottom: ${themes.spacing(4)};
`;

const SectionTitle = styled(Typography)`
  font-size: 2rem;
  margin-bottom: 10px;
  color: ${themes.palette.primary.main};
  text-align: left;
`;

export const NftImg = styled.img`
  border: 1px solid ${themes.palette.primary.main};
  display: block;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

const PriceTypo = styled(Typography)`
  font-size: 18px;
  text-align: center;
  font-weight: 500;
  padding: 20px 0;
  span.ethTxt {
    color: ${themes.palette.primary.main};
    font-weight: 600;
    font-size: 14px;
    padding-left: 4px;
  }

  span.amount {
    font-size: 14px;
    padding-left: 8px;
  }
`;

export const NftImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;
  /* background-color: ${themes.palette.primary.main}; */
`;

const ButtonWrapper = styled.div`
  position: absolute;
  top: -2%;
  left: -2%;
`;

const IconWrqapper = styled.div`
  width: 100px;
  img {
    width: 100%;
  }
`;

const Links = styled.div`
  a {
    color: #000;
    transition: all.3s;
    &:hover {
      opacity: 0.5;
      transition: all.3s;
    }
  }
  span {
    padding: 0 8px;
  }
`;
const MetaMaskWrapper = styled.div`
  display: none;
  @media (max-width: 830px) {
    display: block;
  }
`;

const SnsButtonWrapper = styled.div`
  text-align: center;
  padding-top: ${themes.spacing(2)};
  img {
    width: 26px;
    height: 26px;
    object-fit: contain;
  }
`;
